import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useEntityDynamicConfigWithLoading} from '../useDynamicConfig';

const NGW_LIMITS_CONFIG_ID = 'ngw_truncated_panel_gen_limits';

/** The panel-generation limits, as the config's top level carries them. */
type PanelGenLimits = {
  runLimit: number;
  maxPrefixGroups: number;
  maxMetricsPerGroup: number;
};

export type ProjectInfo = {
  entityName: string;
  projectName: string;
};

export type NgwTruncatedPanelGenLimits = PanelGenLimits & {
  /**
   * Per-project settings, for projects already promoted to the accelerator. An
   * entry cannot promote a project or hold one back -- promotion is decided by
   * the ch_runs_read_from_accelerator gate alone
   * (runrouting.ReadsFromAcceleratorAsPrimary), which never reads this
   * config. Entries exist because a Statsig rule reaches only as far as the
   * user, entity or org it matched, so a setting that varies per project has
   * nowhere else to live -- today just whether to keep the legacy
   * history-keys query. The limits themselves come from the rule.
   *
   * The key stays `eligibleProjects` to match the values already written in the
   * console; it no longer decides eligibility.
   */
  eligibleProjects?: ProjectInfo[];
};

type NgwTruncatedPanelGenLimitsResult = {
  config: PanelGenLimits;
  loading: boolean;
};

const DEFAULT_NGW_TRUNCATED_PANEL_GEN_LIMITS: PanelGenLimits = {
  runLimit: 100,
  maxPrefixGroups: 50,
  maxMetricsPerGroup: 500,
};

/** Returns this project's entry, or undefined if it has none. */
export function findNgwProjectEntry(
  entityName: string | undefined,
  projectName: string | undefined,
  eligibleProjects: ProjectInfo[] | undefined
): ProjectInfo | undefined {
  if (
    entityName == null ||
    projectName == null ||
    entityName === '' ||
    projectName === '' ||
    eligibleProjects == null
  ) {
    return undefined;
  }
  return eligibleProjects.find(
    project =>
      project.entityName === entityName && project.projectName === projectName
  );
}

/**
 * What the config says about the project being viewed: the panel-generation
 * limits in force, and whether to keep the legacy history-keys query.
 *
 * The limits come from the matched Statsig rule, but only for a project the
 * config lists. A rule resolves at the entity, so its values would otherwise
 * reach every project under that entity, including ones no one has looked at.
 * An unlisted project falls back to the defaults here.
 *
 * Read at the project's entity, not the viewer's. The user edge resolves
 * Statsig's entity and org attributes from the viewer's *default* entity
 * (api/resolver/query_dynamic_configs.go UserResolver), so a rule on this
 * config would target whoever is looking rather than what they are looking at.
 * The entity edge passes the entity in the URL and its org, so the limits
 * follow the project on screen.
 */
export const useNgwTruncatedPanelGenLimits =
  (): NgwTruncatedPanelGenLimitsResult => {
    const {entityName, projectName} = useProjectParams();
    const {config, loading} = useEntityDynamicConfigWithLoading(
      NGW_LIMITS_CONFIG_ID,
      entityName ?? ''
    );
    // Splitting the project list off the rule's own values keeps it out of the
    // limits spread below.
    const {eligibleProjects, ...ruleLimits} = (config ??
      {}) as Partial<NgwTruncatedPanelGenLimits>;
    const entry = findNgwProjectEntry(
      entityName,
      projectName,
      eligibleProjects
    );
    return {
      config: {
        ...DEFAULT_NGW_TRUNCATED_PANEL_GEN_LIMITS,
        ...(entry == null ? {} : ruleLimits),
      },
      loading,
    };
  };
